import { NumberUtils } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import { formatDateRangeFromStrings } from '../utils';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface SampleSizeMetric {
  metric: string;
  requiredSampleSize?: number;
  [key: string]: unknown;
}

interface AssignmentFilterValue {
  stringValue?: string;
}

interface ExposureSourceAssignments {
  assignmentTable?: string;
  customStartTime?: string;
  customEndTime?: string;
  filters?: {
    assignmentFilter?: {
      criteria?: {
        flag?: {
          attribute?: {
            setRule?: {
              values?: AssignmentFilterValue[];
            };
            attribute?: string;
          };
        };
      };
      [key: string]: unknown;
    };
    entityFilter?: unknown;
    exposureFilter?: string;
  };
}

interface ExposureSourceExistingInstance {
  workflowInstance: string;
  exposureTableSchedule?: string;
}

type ExposureSource =
  | ExposureSourceAssignments
  | ExposureSourceExistingInstance;

interface SampleSizeData {
  requiredSampleSize?: number;
  metrics?: SampleSizeMetric[];
  exposureSource?: ExposureSource;
  state?: string;
  useScheduledCalculation?: boolean;
  [key: string]: unknown;
}

const isExistingInstance = (
  source?: ExposureSource,
): source is ExposureSourceExistingInstance =>
  source !== undefined && 'workflowInstance' in source;

const getExposureSourceType = (source?: ExposureSource): string => {
  if (!source) return '';
  if (isExistingInstance(source)) return 'Previous experiment';
  return 'Assignments';
};

const getInstanceId = (source: ExposureSourceExistingInstance): string => {
  const parts = source.workflowInstance.split('/');
  return parts[parts.length - 1];
};

const getFlagNames = (source: ExposureSourceAssignments): string[] => {
  const values =
    source.filters?.assignmentFilter?.criteria?.flag?.attribute?.setRule
      ?.values ?? [];
  return values
    .map(v => {
      const name = v.stringValue ?? '';
      return name.startsWith('flags/') ? name.slice(6) : name;
    })
    .filter(Boolean);
};

const sourceChanged = (
  oldSource?: ExposureSource,
  newSource?: ExposureSource,
): boolean => JSON.stringify(oldSource) !== JSON.stringify(newSource);

const SampleSizeChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as SampleSizeData | null;
  const newValue = change.newValue as SampleSizeData | null;

  const oldSource = oldValue?.exposureSource;
  const newSource = newValue?.exposureSource;

  const oldSize = oldValue?.requiredSampleSize;
  const newSize = newValue?.requiredSampleSize;
  const sizeChanged = oldSize !== newSize;

  const oldMetrics = oldValue?.metrics ?? [];
  const newMetrics = newValue?.metrics ?? [];
  const metricsWithChanges = newMetrics.filter(newMetric => {
    const oldMetric = oldMetrics.find(m => m.metric === newMetric.metric);
    return (
      oldMetric?.requiredSampleSize !== newMetric.requiredSampleSize &&
      newMetric.requiredSampleSize !== undefined
    );
  });

  const hasSourceChange = sourceChanged(oldSource, newSource);
  const oldType = getExposureSourceType(oldSource);
  const newType = getExposureSourceType(newSource);
  const typeChanged = hasSourceChange && oldType !== newType;

  // Detail rows for the new exposure source
  const detailRows: {
    tagLabel: string;
    oldValue?: string;
    newValue?: string;
  }[] = [];

  if (hasSourceChange) {
    if (isExistingInstance(newSource)) {
      const newId = getInstanceId(newSource);
      const oldId =
        oldSource && isExistingInstance(oldSource)
          ? getInstanceId(oldSource)
          : undefined;
      if (oldId !== newId) {
        detailRows.push({
          tagLabel: 'Previous experiment',
          oldValue: oldId,
          newValue: newId,
        });
      }
    } else if (newSource && !isExistingInstance(newSource)) {
      const newFlags = getFlagNames(newSource);
      const oldFlags =
        oldSource && !isExistingInstance(oldSource)
          ? getFlagNames(oldSource)
          : [];

      const oldFlagsStr = oldFlags.length > 0 ? oldFlags.join(', ') : undefined;
      const newFlagsStr = newFlags.length > 0 ? newFlags.join(', ') : undefined;

      if (oldFlagsStr !== newFlagsStr && newFlagsStr) {
        detailRows.push({
          tagLabel: 'Filter assignments by flags',
          oldValue: oldFlagsStr,
          newValue: newFlagsStr,
        });
      }

      const oldStart =
        oldSource && !isExistingInstance(oldSource)
          ? oldSource.customStartTime
          : undefined;
      const oldEnd =
        oldSource && !isExistingInstance(oldSource)
          ? oldSource.customEndTime
          : undefined;
      const newStart = newSource.customStartTime;
      const newEnd = newSource.customEndTime;

      if (oldStart !== newStart || oldEnd !== newEnd) {
        const oldRange = formatDateRangeFromStrings(oldStart, oldEnd);
        const newRange = formatDateRangeFromStrings(newStart, newEnd);
        if (oldRange !== newRange && newRange) {
          detailRows.push({
            tagLabel: 'Custom date range',
            oldValue: oldRange || undefined,
            newValue: newRange,
          });
        }
      }
    }
  }

  return (
    <>
      {typeChanged && (
        <ActivityItem.Section label="Updated exposure source">
          <ActivityTagItem oldValue={oldType || undefined} newValue={newType} />
        </ActivityItem.Section>
      )}

      {!typeChanged && hasSourceChange && (
        <ActivityItem.Section label="Updated exposure source">
          <ActivityTagItem defaultValue={newType} />
        </ActivityItem.Section>
      )}

      {detailRows.map(row => (
        <ActivityTagItem
          key={row.tagLabel}
          tagLabel={row.tagLabel}
          oldValue={row.oldValue}
          newValue={row.newValue}
        />
      ))}

      {sizeChanged && (
        <ActivityItem.Section label="Updated sample size">
          <ActivityTagItem
            oldValue={
              NumberUtils.formatNumberWithSeparators(oldSize) || 'Not set'
            }
            newValue={NumberUtils.formatNumberWithSeparators(newSize)}
          />
        </ActivityItem.Section>
      )}

      {metricsWithChanges.map(metric => {
        const oldMetric = oldMetrics.find(m => m.metric === metric.metric);
        return (
          <ActivityTagItem
            key={metric.metric}
            tagLabel={metric.metric}
            oldValue={
              NumberUtils.formatNumberWithSeparators(
                oldMetric?.requiredSampleSize,
              ) || 'Not set'
            }
            newValue={NumberUtils.formatNumberWithSeparators(
              metric.requiredSampleSize,
            )}
          />
        );
      })}
    </>
  );
};

export const SAMPLE_SIZE_CHANGE_EXTENSION =
  createWorkflowUpdatesChangeExtension({
    id: 'sample-size-change',
    predicate: ({ change }) => change.fieldPath === '/module_data/samplesize',
    title: () => 'Updated sample size configuration',
    render: SampleSizeChangeRender,
  });
